import React, { useEffect, useState } from "react";
import { useProjects } from "./useHook";
import Swal from 'sweetalert2';
import { MdDelete } from "react-icons/md";
import { FiEdit2 } from "react-icons/fi";
import { IoEye } from "react-icons/io5";
import { useNavigate, useParams } from "react-router-dom";
import GlobalPagination from '../../Components/Pagination/GlobalPagination';

function OurProjects() {
    const { id } = useParams()
    const { getProjectsData, addProject, getAllProjectCategories, updateProject, deleteProject, toggleProjectStatus } = useProjects()
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [ourProjectsData, setOurProjectsData] = useState([])
    const [projectCategories, setProjectCategories] = useState([])
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingProjectId, setEditingProjectId] = useState(null);
    const [isLoadingProject, setIsLoadingProject] = useState(false);
    const navigate = useNavigate();
    const [pagination, setPagination] = useState({
        totalCount: 0,
        totalPages: 0,
        currentPage: 1,
        limit: 10,
        offset: 0,
        hasNextPage: false,
        hasPreviousPage: false
    });
    const [searchName, setSearchName] = useState("");
    const [formData, setFormData] = useState({
        categoryId: id || '',
        title: '',
        customUrl: '',
        description: '',
        headerImage: null
    });
    const [projectImages, setProjectImages] = useState([]);
    const [existingProjectImages, setExistingProjectImages] = useState([]);
    const [selectedImageIds, setSelectedImageIds] = useState([]);
    const [charCounts, setCharCounts] = useState({
        description: 0
    });

    const countCharacters = (text) => {
        if (!text || typeof text !== 'string') return 0;
        return text.length;
    };

    const openModalForAdd = () => {
        setEditData(null);
        setIsEditMode(false);
        setEditingProjectId(null);
        resetForm();
        setIsModalOpen(true);
    };

    const openModalForEdit = (project) => {
        setEditData(project);
        setIsEditMode(true);
        setEditingProjectId(project.id);
        loadProjectForEdit(project);
        setIsModalOpen(true);
    };

    const loadData = async () => {
        const params = {
            offset: pagination.offset,
            limit: pagination.limit
        };
        if (searchName) {
            params.name = searchName;
        }
        const paginationData = await getProjectsData(setOurProjectsData, id, params);
        if (paginationData) {
            setPagination(paginationData);
        }
    };

    useEffect(() => {
        loadData();
        getAllProjectCategories(setProjectCategories);
    }, [pagination.offset, pagination.limit]);

    useEffect(() => {
        if (formData.description !== undefined) {
            const charCount = countCharacters(formData.description);
            setCharCounts(prev => ({
                ...prev,
                description: charCount
            }));
        }
    }, [formData.description]);

    const handlePageChange = (newOffset) => {
        setPagination(prev => ({
            ...prev,
            offset: newOffset
        }));
    };

    const handleToggleStatus = async (projectId) => {
        try {
            const response = await toggleProjectStatus(projectId)
            if (response) {
                Swal.fire({
                    title: 'Success!',
                    text: 'Project status has been toggled successfully.',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });
            }
            loadData()
        } catch (error) {
            console.log(error)
            Swal.fire({
                title: 'Error!',
                text: 'Failed to toggle project status.',
                icon: 'error'
            });
        }
    }

    const loadProjectForEdit = async (projectData) => {
        setIsLoadingProject(true);
        try {
            const description = projectData.description || '';
            const charCount = countCharacters(description);

            setFormData({
                categoryId: projectData.categoryId || projectData.category?.id || '',
                title: projectData.title || projectData.name || '',
                description: description,
                customUrl: projectData.customUrl || '',
                headerImage: projectData.headerImage ? {
                    preview: projectData.headerImage,
                    file: null
                } : null
            });

            // Load existing project images
            if (projectData.projectImages && Array.isArray(projectData.projectImages)) {
                setExistingProjectImages(projectData.projectImages);
                // Pre-select all existing images
                setSelectedImageIds(projectData.projectImages.map(img => img.id));
            } else {
                setExistingProjectImages([]);
                setSelectedImageIds([]);
            }

            setCharCounts({
                description: charCount
            });
        } catch (error) {
            console.error("Error loading project for edit:", error);
            Swal.fire({
                title: 'Error!',
                text: 'Failed to load project data for editing.',
                icon: 'error'
            });
        } finally {
            setIsLoadingProject(false);
        }
    };

    const handleDelete = async (projectId) => {
        Swal.fire({
            title: 'Are you sure?',
            text: 'You won\'t be able to revert this!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await deleteProject(projectId);
                    loadData();
                    Swal.fire({
                        title: 'Deleted!',
                        text: 'Project has been deleted successfully.',
                        icon: 'success',
                        timer: 2000,
                        showConfirmButton: false
                    });
                } catch (error) {
                    console.error("Error deleting project:", error);
                    Swal.fire({
                        title: 'Error!',
                        text: 'Failed to delete project.',
                        icon: 'error'
                    });
                }
            }
        });
    }
    const handleViewProject = (projectId) => {
        navigate(`/our-projects/view/${projectId}`);
    };

    const handleSearch = async () => {
        setPagination(prev => ({
            ...prev,
            offset: 0
        }));
        const params = {
            offset: 0,
            limit: pagination.limit
        };
        if (searchName) {
            params.name = searchName;
        }
        const paginationData = await getProjectsData(setOurProjectsData, id, params);
        if (paginationData) {
            setPagination(paginationData);
        }
    };

    const handleReset = async () => {
        setSearchName("");
        setPagination(prev => ({
            ...prev,
            offset: 0
        }));
        const paginationData = await getProjectsData(setOurProjectsData, id, {
            offset: 0,
            limit: pagination.limit
        });
        if (paginationData) {
            setPagination(paginationData);
        }
    };
    const handleEditProject = (project) => {
        openModalForEdit(project);
    };

    const handleHeaderImageUpload = async (event) => {
        const file = event.target.files[0];
        if (file) {
            const checkImageResolution = (file) => {
                return new Promise((resolve) => {
                    const img = new Image();
                    img.onload = () => {
                        if (img.width === 450 && img.height === 450) {
                            resolve(true);
                        } else {
                            resolve(false);
                        }
                    };
                    img.src = URL.createObjectURL(file);
                });
            };

            const isValid = await checkImageResolution(file);
            if (isValid) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setFormData(prev => ({
                        ...prev,
                        headerImage: {
                            preview: reader.result,
                            file: file
                        }
                    }));
                };
                reader.readAsDataURL(file);
            } else {
                Swal.fire({
                    title: 'Invalid Image Resolution',
                    text: `Image "${file.name}" does not have the required resolution of 450x450. Please upload the correct resolution.`,
                    icon: 'error'
                });
            }
        }
    };

    const handleProjectImagesUpload = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length > 0) {
            const imageObjects = await Promise.all(
                files.map(async (file) => {
                    return new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                            resolve({
                                preview: reader.result,
                                file: file,
                                name: file.name
                            });
                        };
                        reader.readAsDataURL(file);
                    });
                })
            );
            setProjectImages(prev => [...prev, ...imageObjects]);
        }
    };

    const removeProjectImage = (index) => {
        setProjectImages(prev => prev.filter((_, i) => i !== index));
    };

    const handleImageToggle = (imageId) => {
        setSelectedImageIds(prev =>
            prev.includes(imageId)
                ? prev.filter(id => id !== imageId)
                : [...prev, imageId]
        );
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'description') {
            const charCount = countCharacters(value);

            if (charCount > 925) {
                return;
            }

            setFormData(prev => ({
                ...prev,
                [name]: value
            }));

            setCharCounts(prev => ({
                ...prev,
                description: charCount
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const currentCharCount = countCharacters(formData.description);
        if (currentCharCount > 925) {
            Swal.fire({
                title: 'Error',
                text: `Description cannot exceed 925 characters. Current count: ${currentCharCount}`,
                icon: 'error'
            });
            return;
        }

        if (!formData.headerImage) {
            Swal.fire({
                title: 'Error',
                text: 'Header image is required. Please upload a header image.',
                icon: 'error'
            });
            return;
        }

        setIsSubmitting(true);

        try {
            const formDataObj = new FormData();

            formDataObj.append('title', formData.title);
            formDataObj.append('customUrl', formData.customUrl);
            formDataObj.append('description', formData.description);
            formDataObj.append('categoryId', id);

            if (formData.headerImage?.file) {
                formDataObj.append('headerImage', formData.headerImage.file);
            }

            // Add multiple project images
            projectImages.forEach(image => {
                if (image.file) {
                    formDataObj.append('projectImages', image.file);
                }
            });

            // For edit mode, add keepImageIds
            if (isEditMode) {
                formDataObj.append('keepImageIds', JSON.stringify(selectedImageIds));
            }

            let response;
            if (isEditMode) {
                response = await updateProject(editingProjectId, formDataObj);
            } else {
                response = await addProject(formDataObj);
            }

            resetForm();
            setIsModalOpen(false);

            loadData();

            Swal.fire({
                title: 'Success!',
                text: isEditMode ? 'Project has been updated successfully.' : 'Project has been created successfully.',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });

        } catch (error) {
            console.error('Error saving project:', error);
            Swal.fire({
                title: 'Error!',
                text: isEditMode ? 'Failed to update project. Please try again.' : 'Failed to create project. Please try again.',
                icon: 'error'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const isFormValid = () => {
        const hasRequiredFields = formData.title.trim() !== '' && (formData.categoryId !== '' || id);
        const hasHeaderImage = formData.headerImage !== null;

        return hasRequiredFields && hasHeaderImage;
    };

    const resetForm = () => {
        setFormData({
            categoryId: id || '',
            title: '',
            customUrl: '',
            description: '',
            headerImage: null
        });
        setProjectImages([]);
        setExistingProjectImages([]);
        setSelectedImageIds([]);
        setCharCounts({
            description: 0
        });
        setIsEditMode(false);
        setEditingProjectId(null);
        setEditData(null);
    };

    return (
        <>
            <div className="bg-gray-100 p-4 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-800">Manage Project</h2>
                <div className="flex gap-3">
                    <button
                        className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded shadow"
                        onClick={openModalForAdd}
                    >
                        Add Project
                    </button>
                </div>
            </div>

            <div className="mt-4">
                <div className="bg-white p-4 rounded-md shadow-md mb-6 flex gap-4 mt-4">
                    <input
                        type="text"
                        className="w-[40%] flex-grow px-4 py-2 border rounded-lg"
                        placeholder="Search by name"
                        value={searchName}
                        onChange={(e) => setSearchName(e.target.value)}
                    />
                    <button
                        className="w-full sm:w-[45%] md:w-60 bg-blue-500 text-white px-4 py-2 rounded-md"
                        onClick={handleSearch}
                    >
                        Search
                    </button>
                    <button
                        className="w-full sm:w-[45%] md:w-60 bg-primary-dark text-white px-4 py-2 rounded-md"
                        onClick={handleReset}
                    >
                        Reset
                    </button>
                </div>
            </div>

            <div className="mt-4">
                <div className="overflow-x-auto mt-6">
                    <table className="min-w-full bg-white shadow-md rounded-lg overflow-hidden">
                        <thead>
                            <tr className="bg-gradient-to-r from-primary-dark to-primary-light text-white">
                                <th className="p-4 text-left">Sr. No</th>
                                <th className="p-4 text-left">Name</th>
                                <th className="p-4 text-left">Category</th>
                                <th className="p-4 text-left">Status</th>
                                <th className="p-4 text-left">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ourProjectsData?.map((item, index) => (
                                <tr
                                    key={item?.id}
                                    className="border-t border-gray-200 hover:bg-gray-50"
                                >
                                    <td className="p-4">{index + 1}</td>
                                    <td className="p-4">{item?.title || item?.name}</td>
                                    <td className="p-4">{item?.category?.name}</td>
                                    <td className="p-4">
                                        <div className={`relative inline-block w-16 h-8 rounded-full cursor-pointer ${item?.status ? "bg-primary-dark" : "bg-[#cccccc]"}`} onClick={() => handleToggleStatus(item.id)}>
                                            <div className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transform transition-transform duration-300 ${item?.status ? "translate-x-8" : ""}`}></div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 flex space-x-4">
                                        <button
                                            className="bg-blue-500 text-white py-2 px-4 rounded-lg shadow-md flex items-center hover:bg-blue-600"
                                            onClick={() => handleViewProject(item.id)}
                                        >
                                            <IoEye />
                                        </button>
                                        <button
                                            className="bg-green-500 text-white py-2 px-4 rounded-lg shadow-md flex items-center hover:bg-green-600"
                                            onClick={() => handleEditProject(item)}
                                        >
                                            <FiEdit2 />
                                        </button>
                                        <button
                                            className="bg-red-500 text-white py-2 px-4 rounded-lg shadow-md flex items-center hover:bg-red-600"
                                            onClick={() => handleDelete(item.id)}
                                        >
                                            <MdDelete />
                                        </button>
                                    </td>
                                </tr>
                            ))}

                            {ourProjectsData?.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="text-center py-4 text-gray-500">
                                        No project records found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {pagination.totalPages > 1 && (
                <div className="mt-6">
                    <GlobalPagination
                        offset={pagination.offset}
                        limit={pagination.limit}
                        total={pagination.totalCount}
                        onPageChange={handlePageChange}
                        className="mx-4"
                    />
                </div>
            )}

            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                        {isLoadingProject ? (
                            <div className="p-8 flex justify-center items-center min-h-[400px]">
                                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
                            </div>
                        ) : (
                            <div className="p-8">
                                <div className="flex justify-between items-center mb-8">
                                    <h2 className="text-3xl font-bold text-gray-800">
                                        {isEditMode ? 'Edit Project' : 'Create New Project'}
                                    </h2>
                                    <button
                                        onClick={() => {
                                            setIsModalOpen(false);
                                            resetForm();
                                        }}
                                        className="text-gray-500 hover:text-gray-700 transition-colors"
                                        disabled={isSubmitting}
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-8">
                                    {/* Header Image Section */}
                                    <div>
                                        <label className="block text-lg font-medium text-gray-700 mb-2">
                                            Header Image *
                                        </label>
                                        <div className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-xl transition-colors ${!formData.headerImage
                                            ? 'border-red-300 bg-red-50 hover:border-red-400'
                                            : 'border-gray-300 hover:border-blue-500'
                                            }`}>
                                            <div className="space-y-1 text-center">
                                                {formData.headerImage ? (
                                                    <div className="relative group">
                                                        <img
                                                            src={
                                                                formData.headerImage.preview
                                                                    ? formData.headerImage.preview
                                                                    : formData.headerImage
                                                            }
                                                            alt="Header preview"
                                                            className="mx-auto h-48 w-full object-cover rounded-lg"
                                                        />
                                                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
                                                            <button
                                                                type="button"
                                                                onClick={() => setFormData(prev => ({ ...prev, headerImage: null }))}
                                                                className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors transform scale-0 group-hover:scale-100 duration-300"
                                                            >
                                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <svg
                                                            className="mx-auto h-16 w-16 text-gray-400"
                                                            stroke="currentColor"
                                                            fill="none"
                                                            viewBox="0 0 48 48"
                                                            aria-hidden="true"
                                                        >
                                                            <path
                                                                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                                                strokeWidth={2}
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                        <div className="flex text-sm text-gray-600">
                                                            <label
                                                                htmlFor="header-image"
                                                                className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                                                            >
                                                                <span>Upload a header image</span>
                                                                <input
                                                                    id="header-image"
                                                                    name="header-image"
                                                                    type="file"
                                                                    className="sr-only"
                                                                    accept="image/*"
                                                                    onChange={handleHeaderImageUpload}
                                                                />
                                                            </label>
                                                            <p className="pl-1">or drag and drop</p>
                                                        </div>
                                                        <p className="text-xs text-gray-500">
                                                            PNG, JPG, GIF up to 10MB, 450x450 pixels
                                                            <span className="text-red-400 font-medium"> * Required</span>
                                                        </p>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        {/* {!formData.headerImage && (
                                            <p className="text-red-500 text-sm mt-1">Header image is required</p>
                                        )} */}
                                    </div>

                                    {/* Project specific fields */}
                                    <div className="grid grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-lg font-medium text-gray-700 mb-2">
                                                Title *
                                            </label>
                                            <input
                                                type="text"
                                                name="title"
                                                value={formData.title}
                                                onChange={handleInputChange}
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-lg font-medium text-gray-700 mb-2">
                                                Custom URL
                                            </label>
                                            <input
                                                type="text"
                                                name="customUrl"
                                                value={formData.customUrl}
                                                onChange={handleInputChange}
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Enter custom URL (e.g., my-project-url)"
                                            />
                                        </div>
                                    </div>

                                    {/* Description Section */}
                                    <div>
                                        <label className="block text-lg font-medium text-gray-700 mb-2">
                                            Description (Max 925 characters)
                                        </label>
                                        <textarea
                                            name="description"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                            rows={6}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                                            placeholder="Enter project description..."
                                        />
                                        <div className="flex justify-end mt-1">
                                            <span className={`text-sm ${charCounts.description >= 800 ? 'text-red-500' : 'text-gray-500'}`}>
                                                {charCounts.description}/925 characters
                                            </span>
                                        </div>
                                    </div>

                                    {/* Existing Project Images - Only in Edit Mode */}
                                    {isEditMode && existingProjectImages.length > 0 && (
                                        <div>
                                            <label className="block text-lg font-medium text-gray-700 mb-2">
                                                Existing Project Images
                                            </label>
                                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-2">
                                                {existingProjectImages.map((image) => (
                                                    <div
                                                        key={image.id}
                                                        className={`relative rounded-lg overflow-hidden group transition-all ${
                                                            !selectedImageIds.includes(image.id) ? 'opacity-40' : ''
                                                        }`}
                                                    >
                                                        <img
                                                            src={image.image_url}
                                                            alt={`Project ${image.id}`}
                                                            className="w-full h-32 object-cover"
                                                        />
                                                        
                                                        {/* Delete overlay - shows on hover or when marked for deletion */}
                                                        <div className={`absolute inset-0 transition-opacity ${
                                                            !selectedImageIds.includes(image.id) 
                                                                ? 'bg-red-500 bg-opacity-50' 
                                                                : 'bg-black bg-opacity-0 group-hover:bg-opacity-40'
                                                        }`}>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleImageToggle(image.id)}
                                                                className={`absolute top-2 right-2 p-1.5 rounded-full transition-all ${
                                                                    !selectedImageIds.includes(image.id)
                                                                        ? 'bg-green-500 hover:bg-green-600'
                                                                        : 'bg-red-500 hover:bg-red-600 opacity-0 group-hover:opacity-100'
                                                                } text-white`}
                                                                title={selectedImageIds.includes(image.id) ? 'Delete image' : 'Restore image'}
                                                            >
                                                                {selectedImageIds.includes(image.id) ? (
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                                    </svg>
                                                                ) : (
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                                    </svg>
                                                                )}
                                                            </button>
                                                        </div>
                                                        
                                                        {/* Marked for deletion label */}
                                                        {!selectedImageIds.includes(image.id) && (
                                                            <div className="absolute inset-0 flex items-center justify-center">
                                                                <span className="bg-red-600 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-lg">
                                                                    Will be deleted
                                                                </span>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Primary badge */}
                                                        {image.is_primary && selectedImageIds.includes(image.id) && (
                                                            <span className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded shadow-lg">
                                                                Primary
                                                            </span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                            <p className="text-sm text-gray-500 mt-2">
                                                Hover over an image and click the X button to mark it for deletion
                                            </p>
                                        </div>
                                    )}

                                    {/* Add New Project Images */}
                                    <div>
                                        <label className="block text-lg font-medium text-gray-700 mb-2">
                                            Project Images
                                        </label>
                                        <label 
                                            htmlFor="project-images"
                                            className="mt-1 flex flex-col justify-center items-center px-6 py-8 border-2 border-gray-300 border-dashed rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all cursor-pointer"
                                        >
                                            <svg
                                                className="h-16 w-16 text-gray-400 mb-3"
                                                stroke="currentColor"
                                                fill="none"
                                                viewBox="0 0 48 48"
                                                aria-hidden="true"
                                            >
                                                <path
                                                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                                    strokeWidth={2}
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                            <p className="text-sm font-medium text-gray-700 mb-1">
                                                Click to select images
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                PNG, JPG, GIF - Multiple images supported
                                            </p>
                                            <input
                                                id="project-images"
                                                name="project-images"
                                                type="file"
                                                className="sr-only"
                                                accept="image/*"
                                                multiple
                                                onChange={handleProjectImagesUpload}
                                            />
                                        </label>

                                        {/* Preview New Project Images */}
                                        {projectImages.length > 0 && (
                                            <div className="mt-4">
                                                <p className="text-sm text-gray-600 mb-2">
                                                    {projectImages.length} new image(s) selected
                                                </p>
                                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                                    {projectImages.map((image, index) => (
                                                        <div key={index} className="relative group">
                                                            <img
                                                                src={image.preview}
                                                                alt={image.name}
                                                                className="w-full h-32 object-cover rounded-lg"
                                                            />
                                                            <div className="absolute top-1 right-1">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeProjectImage(index)}
                                                                    className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                                                                >
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                            <p className="text-xs text-gray-600 mt-1 truncate">
                                                                {image.name}
                                                            </p>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Submit Button */}
                                    <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsModalOpen(false);
                                                resetForm();
                                            }}
                                            className="px-6 py-3 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                                            disabled={isSubmitting}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className={`px-6 py-3 rounded-lg transition-colors flex items-center gap-2 ${isSubmitting || !isFormValid()
                                                ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                                                : 'bg-blue-500 text-white hover:bg-blue-600'
                                                }`}
                                            disabled={isSubmitting || !isFormValid()}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                                                    {isEditMode ? 'Updating...' : 'Creating...'}
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                    </svg>
                                                    {isEditMode ? 'Update Project' : 'Create Project'}
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}

export default OurProjects;
